import { getRequestByCommunityId } from '@/api/requests';
import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import { Box, Button, Container, Stack, Typography, useTheme } from '@mui/material';
import Grid from '@mui/system/Unstable_Grid';
import { PaperPlaneTilt as PaperPlaneTiltIcon } from '@phosphor-icons/react/dist/ssr';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import type { Request } from '@/types/v2/request';
import { paths } from '@/paths';
import { useUser } from '@/hooks/v2/use-user';
import { RoleGuard } from '@/components/auth/guards/role-guard';
import { CommunityPageHeader } from '@/components/common/community-page-header';
import { Seo } from '@/components/seo';

import { RequestAttachmentCard } from './components/request-attachment-card';
import { RequestDetailsCard } from './components/request-details-card';
import { RequestHeader } from './components/request-header';
import { RequestQuotesCard } from './components/request-quotes-card';

interface RequestResponse {
  request?: Request | null;
}

export default function RequestPageV2() {
  const { community } = useUser();
  const { requestId } = useParams();
  const theme = useTheme();
  const { data, isLoading, error } = useQuery<RequestResponse>({
    queryKey: ['request', requestId],
    queryFn: async () => {
      const response = await getRequestByCommunityId(community?.id, requestId, {
        params: {
          include: 'unit.owners,unit.currentResidents,unit.unitType,files,requestStatus,requestType',
        },
      });
      return response ? response : { request: null };
    },
    retry: false,
  });

  const request = data?.request;

  // Show request not found error
  if (!isLoading && error?.message === 'Request not found') {
    return (
      <>
        <Seo title="Request Not Found" />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            py: 3,
            backgroundColor: theme.palette.background.default,
            minHeight: '100vh',
          }}
        >
          <Container maxWidth="xl">
            <CommunityPageHeader
              breadcrumbs={[
                {
                  label: 'Home',
                  href: '/',
                },
                {
                  label: 'Requests',
                  href: paths.requests.index,
                },
                {
                  label: 'Request Not Found',
                },
              ]}
              icon={<PaperPlaneTiltIcon size={36} />}
              subtitle="The request you're looking for doesn't exist or has been removed"
              title="Request Not Found"
            />

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 8,
                textAlign: 'center',
              }}
            >
              <Box
                sx={{
                  width: '200px',
                  height: '200px',
                  color: 'primary.main',
                  opacity: 0.8,
                  mb: 4,
                }}
              >
                <PaperPlaneTiltIcon size={200} />
              </Box>

              <Typography color="text.secondary" variant="body1" sx={{ mb: 2 }}>
                Request ID:{' '}
                <code style={{ backgroundColor: 'rgba(0,0,0,0.1)', padding: '2px 6px', borderRadius: '4px' }}>
                  {requestId}
                </code>
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Button variant="outlined" onClick={() => window.history.back()} sx={{ minWidth: 120 }}>
                  Go Back
                </Button>
                <Button variant="contained" href={paths.requests.index} sx={{ minWidth: 120 }}>
                  View All Requests
                </Button>
              </Box>
            </Box>
          </Container>
        </Box>
      </>
    );
  }

  return (
    <>
      <Seo title="Request" />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 3,
          backgroundColor: theme.palette.background.default,
          minHeight: '100vh',
        }}
      >
        <Container maxWidth="xl">
          <Stack spacing={4}>
            <RequestHeader error={error} isLoading={isLoading} request={request} />
            <Grid container spacing={4}>
              <Grid md={7} xs={12}>
                <Stack spacing={4}>
                  <RequestDetailsCard error={error} isLoading={isLoading} request={request!} />
                </Stack>
              </Grid>

              <Grid md={5} xs={12}>
                <Stack spacing={4}>
                  <RoleGuard
                    requiredAccountRoles={[
                      ACCOUNT_ROLES.ACCOUNT_ADMIN,
                      ACCOUNT_ROLES.ACCOUNT_MANAGER,
                      ACCOUNT_ROLES.ACCOUNT_OWNER,
                    ]}
                    requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER]}
                  >
                    <RequestQuotesCard />
                  </RoleGuard>
                  <RequestAttachmentCard error={error} isLoading={isLoading} request={request!} />
                </Stack>
              </Grid>
            </Grid>
          </Stack>
        </Container>
      </Box>
    </>
  );
}
